// src/App.tsx
import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import NotificationsCounterProvider from 'app/features/notifications/NotificationsCounterProvider';
import NotificationsToaster from 'app/features/notifications/NotificationsToaster';
import { store } from 'app/redux/store';
import AppRoutes from 'app/routes';
import { Auth } from 'aws-amplify';
import { ConfirmContinueProvider } from 'Components/ConfirmContinue';
import PopupManager from 'Components/Popups/PopupManager';
import ToolTipProvider from 'Components/Popups/Tooltip/Provider';
import Portal from 'Components/Portal';
import { useEffect, useRef, useState } from 'react';
import { Toaster } from 'react-hot-toast';
import { BrowserRouter } from 'react-router-dom';
import { AuthProvider } from 'utils/AuthProvider';
import ContantsProvider from 'utils/ConstantsProvider';
// import FloatingChat from 'app/features/conversations/FloatingChat/FloatingChat';
// import { FloatingChatProvider } from 'app/features/conversations/FloatingChat/FloatingChatProvider';
// import ConversationsListProvider from 'app/features/conversations/ConversationsListProvider';
import WelcomeBack from 'Components/WelcomeBack';
import './amplify';
import './App.css';
import { ExternalLinkProvider } from 'app/providers/ExternalLinkProvider';
function useDisableWheelScrollOnNumberInputs() {
  useEffect(() => {
    document.addEventListener('wheel', (event) => {
      if (
        document?.activeElement &&
        (document?.activeElement as HTMLInputElement).type === 'number'
      ) {
        (document.activeElement as HTMLInputElement).blur();
      }
    });
  }, []);
}

function App() {
  useDisableWheelScrollOnNumberInputs();

  // This state controls the visibility of the welcome overlay.
  const [showWelcome, setShowWelcome] = useState(false);
  // Reference to track when the document was hidden.
  const hiddenTimestamp = useRef<number | null>(null);

  // On initial load, show the welcome overlay.
  useEffect(() => {
    setShowWelcome(true);
  }, []);

  // When the welcome overlay finishes its animation, hide it.
  const handleContinue = () => {
    setShowWelcome(false);
  };

  // Listen for visibility changes.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // Record the time when the document was hidden.
        hiddenTimestamp.current = Date.now();
      } else if (document.visibilityState === 'visible') {
        // When visible, calculate how long the document was hidden.
        const hiddenFor = hiddenTimestamp.current
          ? Date.now() - hiddenTimestamp.current
          : 0;
        // If hidden for more than 5 seconds, show the welcome overlay.
        if (hiddenFor > 15 * 60000) {
          setShowWelcome(true);
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  return (
    <ExternalLinkProvider>
      <div className="App">
        <BrowserRouter>
          <AuthProvider>
            {/* <FloatingChatProvider> */}
            {/* Welcome overlay displayed when criteria are met */}
            {showWelcome && <WelcomeBack onContinue={handleContinue} />}

            {/* Main application */}
            <PopupManager />
            <ToolTipProvider>
              <ContantsProvider>
                <NotificationsCounterProvider>
                  <NotificationsToaster>
                    <ConfirmContinueProvider>
                      <AppRoutes />
                      <Portal>
                        <Toaster
                          position="top-right"
                          reverseOrder={false}
                          containerStyle={{ zIndex: 50 }}
                        />
                      </Portal>
                    </ConfirmContinueProvider>
                  </NotificationsToaster>
                </NotificationsCounterProvider>
              </ContantsProvider>
            </ToolTipProvider>
            {/* <FloatingChat />
          </FloatingChatProvider> */}
          </AuthProvider>
        </BrowserRouter>
      </div>
    </ExternalLinkProvider>
  );
}

const currentConfig = Auth.configure({
  storage: window.localStorage,
});

export function updateKeyInStore<Key>(
  updateKey: ActionCreatorWithPayload<{ key: Key; value: any }, string>,
) {
  return (payload: { key: Key; value: any }) =>
    store.dispatch(updateKey(payload));
}

export default App;
